import React from "react";
import axios from "axios";
import { toast } from "sonner";
import SiteLayout from "@/components/SiteLayout";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarIcon, Clock, CheckCircle2, ArrowRight } from "lucide-react";
import { format } from "date-fns";
import { SERVICE_OPTIONS, TIME_SLOTS, EVARA } from "@/data/evara";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BookConsultation() {
  const [form, setForm] = React.useState({
    full_name: "",
    email: "",
    phone: "",
    organization: "",
    role: "",
    service_interest: SERVICE_OPTIONS[0],
    message: "",
  });
  const [date, setDate] = React.useState(undefined);
  const [time, setTime] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [submitted, setSubmitted] = React.useState(false);

  const minDate = React.useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!date) {
      toast.error("Please pick a preferred date.");
      return;
    }
    if (!time) {
      toast.error("Please pick a preferred time slot.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        preferred_date: format(date, "yyyy-MM-dd"),
        preferred_time: time,
      };
      await axios.post(`${API}/consultations`, payload);
      toast.success("Consultation request received. We'll be in touch within 1 business day.");
      setSubmitted(true);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(Array.isArray(msg) ? msg[0]?.msg || "Validation error" : msg);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <SiteLayout>
        <section className="section-pad" data-testid="booking-success">
          <div className="max-w-3xl mx-auto card-evara p-12 text-center">
            <div className="mx-auto w-14 h-14 rounded-full bg-[var(--evara-bg-mint)] flex items-center justify-center">
              <CheckCircle2 size={28} className="text-[var(--evara-primary)]" />
            </div>
            <h1 className="font-serif text-3xl md:text-5xl mt-6 leading-tight">Thank you.</h1>
            <p className="mt-5 text-[var(--evara-text-2)]">
              Your consultation request has been received. A member of the EVARA team will reach out within one business day to confirm your slot on{" "}
              <span className="font-serif italic text-[var(--evara-primary)]">
                {date ? format(date, "EEEE, d MMMM yyyy") : ""}
              </span>{" "}
              at <span className="font-serif italic text-[var(--evara-primary)]">{time}</span>.
            </p>
            <p className="mt-2 text-sm text-[var(--evara-muted)]">
              For anything urgent, write to{" "}
              <a href={`mailto:${EVARA.contact.email}`} className="hairline-link">
                {EVARA.contact.email}
              </a>.
            </p>
            <button className="btn-secondary mt-8" onClick={() => setSubmitted(false)} data-testid="booking-new-btn">
              Submit another request <ArrowRight size={16} />
            </button>
          </div>
        </section>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <section className="section-pad bg-sage-gradient" data-testid="book-hero">
        <div className="max-w-6xl mx-auto grid md:grid-cols-12 gap-12 items-start">
          <div className="md:col-span-5">
            <div className="eyebrow">Schedule a Consultation</div>
            <h1 className="font-serif text-4xl md:text-5xl mt-5 leading-tight">
              Tell us what you’re solving.<br />
              <em className="not-italic text-[var(--evara-primary)] font-medium">We’ll come prepared.</em>
            </h1>
            <p className="mt-6 text-[var(--evara-text-2)] leading-relaxed">
              A 30-minute discovery call with EVARA leadership. We use the time to understand your priorities, share relevant frameworks and identify quick wins. No deck, no obligation.
            </p>
          </div>

          <form onSubmit={submit} className="md:col-span-7 card-evara p-7 md:p-9 space-y-6" data-testid="booking-form">
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="evara-label">Full name</label>
                <input className="evara-input" required value={form.full_name} onChange={onChange("full_name")} placeholder="Your name" data-testid="form-full-name" />
              </div>
              <div>
                <label className="evara-label">Work email</label>
                <input className="evara-input" type="email" required value={form.email} onChange={onChange("email")} placeholder="you@hospital.com" data-testid="form-email" />
              </div>
              <div>
                <label className="evara-label">Phone</label>
                <input className="evara-input" required value={form.phone} onChange={onChange("phone")} placeholder="+91" data-testid="form-phone" />
              </div>
              <div>
                <label className="evara-label">Role</label>
                <input className="evara-input" value={form.role} onChange={onChange("role")} placeholder="e.g. COO, Quality Head" data-testid="form-role" />
              </div>
              <div className="md:col-span-2">
                <label className="evara-label">Organisation</label>
                <input className="evara-input" value={form.organization} onChange={onChange("organization")} placeholder="Hospital / IVF Centre / Group name" data-testid="form-organization" />
              </div>
              <div className="md:col-span-2">
                <label className="evara-label">Service of interest</label>
                <select className="evara-input" value={form.service_interest} onChange={onChange("service_interest")} data-testid="form-service-interest">
                  {SERVICE_OPTIONS.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="divider-rule" />

            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="evara-label">Preferred date</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="evara-input flex items-center justify-between text-left"
                      data-testid="form-date-trigger"
                    >
                      <span className={date ? "text-[var(--evara-text)]" : "text-[var(--evara-muted)]"}>
                        {date ? format(date, "EEEE, d MMM yyyy") : "Select a date"}
                      </span>
                      <CalendarIcon size={16} className="text-[var(--evara-primary)]" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="start" className="w-auto p-0 bg-white border-[var(--evara-border)]">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={setDate}
                      disabled={(d) => d < minDate}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div>
                <label className="evara-label">Preferred time</label>
                <div className="relative">
                  <select className="evara-input pr-9" value={time} onChange={(e) => setTime(e.target.value)} data-testid="form-time-select">
                    <option value="">Select a time slot</option>
                    {TIME_SLOTS.map((t) => (
                      <option key={t} value={t}>{t} IST</option>
                    ))}
                  </select>
                  <Clock size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--evara-primary)] pointer-events-none" />
                </div>
              </div>
            </div>

            <div>
              <label className="evara-label">What would you like to discuss?</label>
              <textarea className="evara-input" rows={5} value={form.message} onChange={onChange("message")} placeholder="A short note about your priorities, current challenges or goals…" data-testid="form-message" />
            </div>

            <div className="flex items-center justify-between flex-wrap gap-3 pt-2">
              <p className="text-xs text-[var(--evara-muted)] max-w-sm">
                By submitting, you agree EVARA may contact you to schedule a discovery call. We never share your details.
              </p>
              <button type="submit" className="btn-primary" disabled={loading} data-testid="form-submit-btn">
                {loading ? "Submitting…" : "Request consultation"} {!loading && <ArrowRight size={16} />}
              </button>
            </div>
          </form>
        </div>
      </section>
    </SiteLayout>
  );
}
